import { useState, useEffect, useMemo, useRef } from 'react';
import { ISearchBarData } from '../../state/api/types';
import Presenter from './Presenter';
import { IContainerProps } from './types';
import { IBusiness, IService } from '../../state/api/types';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from '../../state/types';
import selectors from '../../state/selectors';
import actions from '../../state/actions';
import { useAlert } from '../../state/Alerts/AlertsContext';
import { loadBookingState } from '../../utils/bookingPersistence';

interface ILocationState {
    navSelectedBusinessId?: string;
}

const BusinessDetailContainer = (props: IContainerProps) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const { showAlert } = useAlert();
    const { businessName} = useParams<{ businessName: string }>(); // Get the business name from the URL
    const { navSelectedBusinessId } = (location.state as ILocationState) || {};
    const [isShareModalVisible, setShareModalVisibility] = useState<boolean>(false);

    // New: Booking flow visibility with history management
    const [isBookingFlowVisible, setBookingFlowVisible] = useState<boolean>(false);
    const [isRequestFormVisible, setRequestFormVisible] = useState<boolean>(false);

    // Keep only business-related state (booking state moved to BookingContext)
    const businesses = useSelector((state: IAppState) => selectors.api.getBusinesses(state));
    const selectedServices = useSelector((state: IAppState) => selectors.api.getSelectedServices(state));
    const bookings = useSelector((state: IAppState) => selectors.api.getBusinessBookings(state));
    const authUser = useSelector((state: IAppState) => selectors.auth.getAuthUser(state));
    const rehydrated = useSelector((state: IAppState) => selectors.auth.getRehydrated(state));

    const [isLoaderVisible, setLoaderVisibility] = useState<boolean>(true);
    const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
    const [isBusinessNotFound, setIsBusinessNotFound] = useState<boolean>(false);
    const [notFoundReason, setNotFoundReason] = useState<string>('');
    const [apiError, setApiError] = useState<string | null>(null);

    // History management for booking flow and request form
    useEffect(() => {
        const handlePopState = (event: PopStateEvent) => {
            if (isBookingFlowVisible) {
                setBookingFlowVisible(false);
            }
            if (isRequestFormVisible) {
                setRequestFormVisible(false);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isBookingFlowVisible, isRequestFormVisible]);

    const toggleBookingFlow = () => {
        if (!isBookingFlowVisible) {
            // Opening booking flow - add history entry
            window.history.pushState({ bookingFlow: true }, '', '');
            setBookingFlowVisible(true);
        } else {
            // Closing booking flow - go back if we added history
            if (window.history.state?.bookingFlow) {
                window.history.back();
            }
            setBookingFlowVisible(false);
        }
    };

    const toggleRequestForm = () => {
        if (!isRequestFormVisible) {
            window.history.pushState({ requestForm: true }, '', '');
            setRequestFormVisible(true);
        } else {
            if (window.history.state?.requestForm) {
                window.history.back();
            }
            setRequestFormVisible(false);
        }
    };

    const retryLoadingBusinesses = () => {
        setApiError(null);
        setIsBusinessNotFound(false);
        setLoaderVisibility(true);
        dispatch(actions.api.getBusinesses((result: any, error: any) => {
            if (error) {
                setApiError('Error al cargar la información. Por favor, intenta nuevamente.');
                setLoaderVisibility(false);
            }
        }));
    };

    useEffect(() => {
        dispatch(actions.api.getBusinesses((result: any, error: any) => {
            if (error) {
                setApiError('Error al cargar la información. Por favor, intenta nuevamente.');
                setLoaderVisibility(false);
            }
        }));
    }, []);

    // Find the business based on either the navSelectedBusinessId or decoded business name
    const selectedBusiness = useMemo(() => {
        if (businesses && businesses.length > 0) {
            const decodedBusinessName = decodeURIComponent(businessName).replace(/-/g, ' ');
            return businesses.find(
                (business: IBusiness) =>
                    business.id === navSelectedBusinessId ||
                    business.name.toLowerCase() === decodedBusinessName.toLowerCase()
            );
        }
        return undefined;
    }, [businesses, navSelectedBusinessId, businessName]);

    // Handle side effects for business loading and not found state
    useEffect(() => {
        if (businesses && businesses.length > 0) {
            setLoaderVisibility(false);

            const decodedBusinessName = decodeURIComponent(businessName).replace(/-/g, ' ');
            if (!selectedBusiness) {
                setIsBusinessNotFound(true);
                setNotFoundReason(navSelectedBusinessId
                    ? 'El negocio seleccionado ya no está disponible'
                    : `No se encontró ningún negocio con el nombre "${decodedBusinessName}"`
                );
            } else {
                setIsBusinessNotFound(false);
                setNotFoundReason('');
            }
        }
    }, [businesses, selectedBusiness, navSelectedBusinessId, businessName]);

    // Removed: timeslot calculation now happens in BookingFlow

    useEffect(() => {
        if (selectedBusiness) {
            dispatch(actions.api.clearSelectedBusinessServices());
        }
    }, [selectedBusiness, dispatch]);

    useEffect(() => {
        // Set a timeout to show error if businesses don't load
        const timeoutId = setTimeout(() => {
            if (isLoaderVisible && (!businesses || businesses.length === 0)) {
                setApiError('La carga está tomando más tiempo del esperado. Por favor, verifica tu conexión.');
                setLoaderVisibility(false);
            }
        }, 10000); // 10 seconds timeout

        return () => clearTimeout(timeoutId);
    }, [isLoaderVisible, businesses]);

    // Fetch business with bookings when business is found
    useEffect(() => {
        if (selectedBusiness && selectedBusiness.id) {
            dispatch(actions.api.getBusinessById(selectedBusiness.id));
        }
    }, [selectedBusiness?.id, dispatch]);

    // Track if we've already auto-opened to prevent loops
    const hasAutoOpenedRef = useRef(false);

    // Auto-open booking modal if returning from auth with saved state
    useEffect(() => {
        if (!selectedBusiness || hasAutoOpenedRef.current) return;

        const saved = loadBookingState(selectedBusiness.id);
        if (saved) {
            setBookingFlowVisible(true);
            hasAutoOpenedRef.current = true;
            // Don't clear state here - let BookingFlow/Presenter handle restoration
        }
    }, [selectedBusiness?.id]);

    // Removed: All booking handlers now in BookingFlow component via BookingContext

    const toggleShareModalVisibility = () => setShareModalVisibility(!isShareModalVisible);

    // Removed: Date marking logic now in BookingFlow if needed

    const totalPrice = selectedServices.reduce((sum, service) => sum + (service.price || 0), 0);

    return (
        <Presenter
            {...props}
            // Business detail display
            selectedBusiness={selectedBusiness}
            selectedServices={selectedServices}
            isLoaderVisible={isLoaderVisible}
            isBusinessNotFound={isBusinessNotFound}
            notFoundReason={notFoundReason}
            apiError={apiError}
            retryLoadingBusinesses={retryLoadingBusinesses}
            totalPrice={totalPrice}

            // Business actions
            isShareModalVisible={isShareModalVisible}
            toggleShareModalVisibility={toggleShareModalVisibility}
            toggleSelectedService={(service: IService) =>
                dispatch(actions.api.toggleSelectedBusinessService(selectedBusiness!, service))
            }

            // Booking flow
            isBookingFlowVisible={isBookingFlowVisible}
            toggleBookingFlow={toggleBookingFlow}
            bookings={bookings}

            // Request form (for VARIABLE services)
            isRequestFormVisible={isRequestFormVisible}
            toggleRequestForm={toggleRequestForm}

            // Auth modal
            showAuthModal={showAuthModal}
            setShowAuthModal={setShowAuthModal}
        />
    );
};

export default BusinessDetailContainer;
